
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { sanitizeHtml, sanitizeInput } from "@/utils/sanitization";
import { Database } from "@/integrations/supabase/types";

type EntryInsert = Database["public"]["Tables"]["entries"]["Insert"];

export const useAutosave = (
  entry: EntryInsert & { is_pinned?: boolean },
  isNewEntry: boolean,
  loading: boolean,
  userId?: string
) => {
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const contentChangedRef = useRef(false);

  const handleAutosave = async () => {
    if (isNewEntry || !entry.id || !userId) return;
    
    if (!entry.title?.trim()) return;
    
    try {
      setAutoSaving(true);
      
      const sanitizedContent = sanitizeHtml(entry.content || '');
      
      const { error } = await supabase
        .from("entries")
        .update({
          title: sanitizeInput(entry.title),
          content: sanitizedContent,
          entry_type: entry.entry_type,
          is_public: entry.is_public,
          is_favorite: entry.is_favorite,
          is_pinned: entry.is_pinned,
          password: entry.password,
          updated_at: new Date().toISOString(),
        })
        .eq("id", entry.id);

      if (error) throw error;
      
      const now = new Date();
      setLastSaved(now);
      contentChangedRef.current = false;
      
      toast.success("Changes autosaved", {
        duration: 2000,
      });
    } catch (error: any) {
      console.error("Error autosaving entry:", error);
    } finally {
      setAutoSaving(false);
    }
  };

  useEffect(() => {
    if (!isNewEntry && !loading && userId && entry.id) {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }

      if (contentChangedRef.current) {
        autosaveTimerRef.current = setTimeout(() => {
          handleAutosave();
        }, 5000);
      }
    }

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [entry, loading, userId, isNewEntry]);

  const markContentChanged = () => {
    contentChangedRef.current = true;
  };

  // Update last saved when entry is loaded
  useEffect(() => {
    if (!isNewEntry && entry.updated_at) {
      setLastSaved(new Date(entry.updated_at));
    }
  }, [entry.updated_at, isNewEntry]);

  return {
    lastSaved,
    autoSaving,
    markContentChanged
  };
};
